import React, { useState, useEffect } from 'react';
import SingleMusic from './SingleMusic';
import Modal from './Modal';

const baseUrl = process.env.REACT_APP_BASE_URL;

// Card component with black background and white/light grey text
const Card = ({ id, title, album, thumbnail, onClick, className }) => (
  <div
    className={`group bg-black text-white shadow-xl overflow-hidden rounded-lg cursor-pointer transition-all duration-500 
      hover:shadow-2xl hover:scale-[1.02] ${className}`}
    onClick={() => onClick(id)}
  >
    <div className="relative overflow-hidden">
      <img 
        src={thumbnail} 
        alt={title} 
        className="xl:h-[80%] w-full object-cover transition-transform duration-700 group-hover:scale-105" 
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
    <div className="p-6">
      <h2 className="text-xl md:text-2xl font-ettlora tracking-tight text-white mb-2 group-hover:text-white/90 transition-colors">
        {title}
      </h2>
      <p className="text-gray-300 text-sm tracking-wide uppercase font-medium">{album}</p>
    </div>
  </div>
);

const FeaturedMusic = () => {
  const [musicData, setMusicData] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedCards, setLoadedCards] = useState([]);

  useEffect(() => {
    const fetchFeaturedMusic = async () => {
      setIsLoading(true);

      try {
        const response = await fetch(`${baseUrl}/api/music/featured`);
        if (!response.ok) throw new Error('Network response was not ok');

        const musicArray = await response.json();

        const updatedMusicData = await Promise.all(musicArray.map(async (music) => {
          const albumResponse = await fetch(`${baseUrl}/api/album/${music.albumID}`);
          const albumData = await albumResponse.json();
          const albumName = albumData.name ? albumData.name : 'Unknown Album';

          return {
            id: music.ID,
            title: music.name,
            album: albumName,
            thumbnail: `${baseUrl}${music.image_video}`,
            youtube_link: music.youtube_link,
            spotify_link: music.spotify_link,
            apple_music_link: music.apple_music_link,
            itunes_store_link: music.itunes_store_link,
            amazon_music_link: music.amazon_music_link,
            deezer_link: music.deezer_link,
          };
        }));

        setMusicData(updatedMusicData);
        
        updatedMusicData.forEach((_, index) => {
          setTimeout(() => {
            setLoadedCards((prev) => [...prev, index]);
          }, index * 100);
        });
        
      } catch (error) {
        console.error('Error fetching music data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedMusic();
  }, []);

  const handleCardClick = (id) => {
    const music = musicData.find((music) => music.id === id);
    setSelectedMusic(music);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedMusic(null);
    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-black transition-colors duration-300">
      
      {/* Music Cards Container */}
      <div className="w-full flex justify-center items-center m-4">
        <h1 className="text-[52px] font-glsnecb md:text-[80px]">FEATURED MUSIC</h1>
      </div>

      {/* Music Cards Grid */}
      <div className="w-[80%] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2 p-4 pb-20">
        
        {isLoading && (
          <div className="col-span-3 flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-black/20 dark:border-white/20 border-t-black dark:border-t-white rounded-full animate-spin mb-4" />
            <p className="text-[11px] tracking-[0.35em] uppercase text-gray-500 dark:text-gray-400">
              Loading...
            </p>
          </div>
        )}

        {!isLoading && musicData.length === 0 && (
          <div className="col-span-3 flex flex-col items-center justify-center py-20">
            <p className="text-[11px] tracking-[0.35em] uppercase text-gray-500 dark:text-gray-400">
              No featured music available
            </p>
          </div>
        )}

        {musicData.map((music, index) => (
          <div 
            key={music.id} 
            className="p-4"
          >
            <div className={`transform transition-all duration-700 ${
              loadedCards.includes(index) 
                ? 'translate-y-0 opacity-100' 
                : 'translate-y-8 opacity-0'
            }`}>
              <Card
                id={music.id}
                title={music.title}
                album={music.album}
                thumbnail={music.thumbnail}
                onClick={handleCardClick}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Modal show={isModalOpen} onClose={closeModal}>
        {selectedMusic && <SingleMusic music={selectedMusic} />}
      </Modal>
      
    </div>
  );
};

export default FeaturedMusic;